// Caret pixel position for <input>/<textarea>, via the well-known mirror-div technique
// (adapted from textarea-caret-position by Jonathan Ong, MIT). The browser exposes no API
// for the caret rect of a form control, so we render an off-screen div that mirrors the
// control's text + styles up to the caret and measure a marker span.

const MIRROR_PROPS = [
  'direction',
  'boxSizing',
  'width',
  'height',
  'overflowX',
  'overflowY',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
] as const

export interface CaretCoordinates {
  /** Offset from the control's top, in px (not yet adjusted for the control's own scroll). */
  top: number
  /** Offset from the control's left, in px. */
  left: number
  /** Line height at the caret, in px. */
  height: number
}

export const getCaretCoordinates = (
  element: HTMLTextAreaElement | HTMLInputElement,
  position: number,
): CaretCoordinates => {
  const isInput = element.nodeName.toLowerCase() === 'input'
  const div = document.createElement('div')
  document.body.appendChild(div)
  const { style } = div
  const computed = window.getComputedStyle(element)

  style.whiteSpace = 'pre-wrap'
  if (!isInput) style.overflowWrap = 'break-word'
  style.position = 'absolute'
  style.visibility = 'hidden'

  for (const prop of MIRROR_PROPS) {
    if (isInput && prop === 'lineHeight') {
      // Single-line inputs render text on one line of height `height`.
      if (computed.boxSizing === 'border-box') {
        const h = parseInt(computed.height, 10) || 0
        const outer =
          (parseInt(computed.borderTopWidth, 10) || 0) +
          (parseInt(computed.borderBottomWidth, 10) || 0) +
          (parseInt(computed.paddingTop, 10) || 0) +
          (parseInt(computed.paddingBottom, 10) || 0)
        style.lineHeight = `${h - outer}px`
      } else {
        style.lineHeight = computed.height
      }
    } else {
      style.setProperty(prop, computed.getPropertyValue(prop))
    }
  }

  div.textContent = element.value.substring(0, position)
  if (isInput) div.textContent = div.textContent.replace(/\s/g, ' ')

  const span = document.createElement('span')
  // The textContent must be non-empty for the span to have a measurable position.
  span.textContent = element.value.substring(position) || '.'
  div.appendChild(span)

  const lineHeight = parseInt(computed.lineHeight, 10)
  const coordinates: CaretCoordinates = {
    top: span.offsetTop + (parseInt(computed.borderTopWidth, 10) || 0),
    left: span.offsetLeft + (parseInt(computed.borderLeftWidth, 10) || 0),
    height: Number.isNaN(lineHeight) ? parseInt(computed.fontSize, 10) || 16 : lineHeight,
  }

  document.body.removeChild(div)
  return coordinates
}

/**
 * Viewport coordinates (for `position: fixed`) just below the caret of a form control —
 * accounts for the control's bounding rect and its internal scroll. Returns null if the
 * caret position can't be measured (e.g. no layout engine).
 */
export const getCaretViewportPosition = (
  element: HTMLTextAreaElement | HTMLInputElement,
  position: number,
): { top: number; left: number } | null => {
  try {
    const rect = element.getBoundingClientRect()
    const caret = getCaretCoordinates(element, position)
    return {
      top: rect.top + caret.top - element.scrollTop + caret.height,
      left: rect.left + caret.left - element.scrollLeft,
    }
  } catch {
    return null
  }
}
